import * as React from "react";
import { NavLink, useLocation } from "react-router-dom";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
} from "@/components/ui/sheet";
import { Menu, ListFilter, Settings } from "lucide-react";
import { useAuth } from "@/hooks/use-auth";

interface SidebarProps {
  className?: string;
}

interface NavItem {
  title: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
  isActive?: (pathname: string) => boolean;
}

const navItems: NavItem[] = [
  {
    title: "Listings",
    href: "/",
    icon: ListFilter,
    isActive: (pathname) => pathname === "/" || pathname === "/listings" || pathname.startsWith("/listings/"),
  },
  { title: "Settings", href: "/settings", icon: Settings },
];

export function Sidebar({ className }: SidebarProps) {
  const location = useLocation();
  const [open, setOpen] = React.useState(false);
  const { user } = useAuth();

  const userName = user?.name || "User";
  const userEmail = user?.email || "No email";
  const userInitial = userName.charAt(0).toUpperCase();

  const NavContent = () => (
    <>
      <div className="px-4 h-16 flex items-center border-b border-[#e5e5e5]">
        <NavLink to="/" className="flex items-center gap-2">
          <div className="h-8 w-8 rounded-md bg-[#000000] flex items-center justify-center">
            <span className="text-[#ffffff] font-bold text-sm">W</span>
          </div>
          <span className="font-semibold text-lg text-[#000000]">WillScraper</span>
        </NavLink>
      </div>
      <ScrollArea className="flex-1 px-3 py-4">
        <nav className="flex flex-col gap-1">
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive = item.isActive ? item.isActive(location.pathname) : location.pathname === item.href;
            return (
              <NavLink
                key={item.href}
                to={item.href}
                onClick={() => setOpen(false)}
                className={cn(
                  "flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors",
                  isActive
                    ? "bg-[#000000] text-[#ffffff]"
                    : "text-[#333333] hover:bg-[#f5f5f5] hover:text-[#000000]"
                )}
              >
                <Icon className="h-4 w-4" />
                {item.title}
              </NavLink>
            );
          })}
        </nav>
      </ScrollArea>
      <div className="border-t border-[#e5e5e5] p-4">
        <div className="flex items-center gap-3 rounded-md bg-[#f5f5f5] p-3">
          <div className="h-8 w-8 rounded-full bg-[#000000] flex items-center justify-center">
            <span className="text-[#ffffff] text-sm font-medium">{userInitial}</span>
          </div>
          <div className="flex flex-col min-w-0">
            <span className="text-sm font-medium text-[#000000] truncate">{userName}</span>
            <span className="text-xs text-[#666666] truncate">{userEmail}</span>
          </div>
        </div>
      </div>
    </>
  );

  return (
    <>
      {/* Mobile Sidebar */}
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetTrigger asChild className="lg:hidden">
          <Button
            variant="ghost"
            size="icon"
            className="fixed left-4 top-4 z-40 lg:hidden"
          >
            <Menu className="h-5 w-5 text-[#000000]" />
            <span className="sr-only">Toggle menu</span>
          </Button>
        </SheetTrigger>
        <SheetContent side="left" className="w-[280px] p-0 bg-[#ffffff]">
          <div className="flex h-full flex-col">
            <NavContent />
          </div>
        </SheetContent>
      </Sheet>

      {/* Desktop Sidebar */}
      <aside
        className={cn(
          "hidden lg:flex h-screen w-[280px] flex-col border-r border-[#e5e5e5] bg-[#ffffff] fixed left-0 top-0",
          className
        )}
      >
        <NavContent />
      </aside>
    </>
  );
}

export default Sidebar;
